import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { servicesAPI } from '../../api'
import { useBooking } from '../../context/BookingContext'
import { RiCheckLine, RiArrowRightLine, RiTimeLine, RiPriceTag3Line } from 'react-icons/ri'
import toast from 'react-hot-toast'

export default function SelectServices() {
  const { booking, setServices, nextStep } = useBooking()
  const [activeCategory, setActiveCategory] = useState('all')

  const { data: categories } = useQuery({
    queryKey: ['service-categories'],
    queryFn: () => servicesAPI.getCategories(),
    select: (r) => r.data?.data || [],
  })

  const { data: services, isLoading } = useQuery({
    queryKey: ['services', activeCategory],
    queryFn: () => servicesAPI.getAll({ category: activeCategory !== 'all' ? activeCategory : undefined }),
    select: (r) => r.data?.data || [],
  })

  const isSelected = (s) => booking.services.some((b) => b.id === s.id)

  const toggleService = (service) => {
    if (isSelected(service)) {
      setServices(booking.services.filter((s) => s.id !== service.id))
    } else {
      setServices([...booking.services, service])
    }
  }

  const handleNext = () => {
    if (booking.services.length === 0) {
      toast.error('Please select at least one service')
      return
    }
    nextStep()
  }

  return (
    <div>
      <h2 className="font-display text-salon-dark text-2xl font-bold mb-1">Choose Services</h2>
      <p className="text-salon-muted font-body text-sm mb-6">You can select multiple services for a single appointment.</p>

      {/* Category tabs */}
      <div className="flex gap-2 flex-wrap mb-6">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold font-body transition-all ${
            activeCategory === 'all' ? 'bg-salon-primary text-white' : 'bg-salon-cream text-salon-muted hover:text-salon-primary'
          }`}
        >All</button>
        {categories?.map((c) => (
          <button key={c.id}
            onClick={() => setActiveCategory(c.id)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold font-body transition-all ${
              activeCategory === c.id ? 'bg-salon-primary text-white' : 'bg-salon-cream text-salon-muted hover:text-salon-primary'
            }`}
          >{c.name}</button>
        ))}
      </div>

      {/* Services grid */}
      {isLoading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 max-h-[420px] overflow-y-auto pr-1">
          {(services || []).map((service) => {
            const selected = isSelected(service)
            return (
              <button
                key={service.id}
                onClick={() => toggleService(service)}
                className={`text-left p-5 rounded-2xl border-2 transition-all duration-200 relative ${
                  selected
                    ? 'border-salon-primary bg-salon-primary/5 shadow-salon-sm'
                    : 'border-salon-cream-dark bg-white hover:border-salon-primary/40'
                }`}
              >
                {selected && (
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-salon-primary flex items-center justify-center">
                    <RiCheckLine className="text-white text-sm" />
                  </div>
                )}
                <p className="font-semibold font-body text-salon-dark mb-1 pr-8">{service.name}</p>
                <p className="text-salon-muted text-xs font-body leading-relaxed mb-3 line-clamp-2">{service.description}</p>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1 text-salon-secondary font-bold text-sm font-body">
                    <RiPriceTag3Line /> ₹{parseFloat(service.price).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1 text-salon-muted text-xs font-body">
                    <RiTimeLine /> {service.duration_minutes} min
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {/* Selected count */}
      {booking.services.length > 0 && (
        <div className="mt-4 p-3 rounded-xl bg-salon-primary/5 border border-salon-primary/20 text-sm text-salon-primary font-body font-medium">
          {booking.services.length} service{booking.services.length > 1 ? 's' : ''} selected
          — Total: ₹{booking.services.reduce((a, s) => a + parseFloat(s.price), 0).toLocaleString()}
        </div>
      )}

      <div className="flex justify-end mt-8">
        <button onClick={handleNext} className="btn-primary">
          Continue <RiArrowRightLine />
        </button>
      </div>
    </div>
  )
}
